import { Component, h, State, Prop, Listen, Method } from "@stencil/core"
import anime from "animejs"
import { isTouchscreen } from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-scroll-view",
  styleUrl: "scroll-view.scss",
  shadow: true,
})
export class ScrollView {
  private componentEl?: HTMLDivElement
  private scrollableEl?: HTMLDivElement
  private slotEl?: HTMLDivElement

  private currentAnimation?: anime.AnimeInstance
  private resizeTimer?: NodeJS.Timer
  private scrollingTimer?: NodeJS.Timer
  private componentDidLoadComplete = false

  @State() private showLeftArrow = false
  @State() private showRightArrow = false

  /**
   * Don't show the arrows (not recommended by UX).
   * @private
   */
  @Prop() public arrowsHidden = false

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated: boolean = true

  /**
   * The top position in px of the arrows (needed in sdx-progress-full).
   * @private
   */
  @Prop() public arrowPositionTop?: number

  @Listen("resize", { target: "window" })
  public onWindowResizeDebounced() {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }

    this.resizeTimer = setTimeout(() => {
      if (!this.scrollableEl) {
        return
      }

      this.showArrowsIfNeeded()

      const { clientWidth, scrollWidth } = this.scrollableEl
      const hasEnoughSpace = clientWidth === scrollWidth

      // When enough space, scroll back to beginning
      if (hasEnoughSpace) {
        this.translateX(-this.getOverflow("left"), false)
      }
    }, 10)
  }

  /**
   * Scrolls till the element is centered in parent.
   * @param HTMLElement The parent element in which the element should be centered.
   * @param HTMLElement The element which should be centered.
   * @param number The gutter.
   * @param boolean Controls if scrolling is animated or not.
   * @returns {Promise} Resolved when scroll animation is done.
   * @private
   */
  @Method()
  public async centerElement(
    parent: HTMLElement,
    toBeCentered: HTMLElement,
    gutter = 0,
    animated = true
  ) {
    if (!parent || !toBeCentered) {
      return
    }

    const componentElRect = parent.getBoundingClientRect()
    const clickableElRect = toBeCentered.getBoundingClientRect()
    const offsetLeft = componentElRect.left
    const clickableElLeft = clickableElRect.left - offsetLeft

    const componentElCenter = componentElRect.width / 2
    const clickableElCenter = clickableElRect.width / 2

    const hasItemElOverflow = {
      toLeft: componentElRect.left - (clickableElRect.left - gutter) > 0,
      toRight: clickableElRect.right + gutter - componentElRect.right > 0,
    }

    // Scroll to selected tab when it's out of sight
    if (hasItemElOverflow.toLeft || hasItemElOverflow.toRight) {
      this.translateX(
        clickableElLeft - componentElCenter + clickableElCenter,
        animated
      )
    }
  }

  /**
   * Scroll to the left or to the right.
   * @param number The distance in px. Negative numbers causes left scroll.
   * @param boolean Controls if scrolling is animated or not.
   * @returns {Promise} Resolved when scroll animation is done.
   * @private
   */
  @Method()
  public async translateX(translateX: number, animated = true): Promise<void> {
    if (!this.currentAnimation?.completed) {
      await this.currentAnimation?.finished
    }

    const scrollableEl = this.scrollableEl!
    const overflowToLeft = this.getOverflow("left")
    const overflowToRight = this.getOverflow("right")

    if (translateX > 0) {
      // to right
      translateX = Math.min(translateX, overflowToRight)
    } else {
      // to left
      translateX = Math.max(translateX, -overflowToLeft)
    }

    // Animation starts
    this.currentAnimation = anime({
      targets: this.scrollableEl,
      easing: "easeOutQuad",
      duration: animated ? this.getAnimationDuration() : 0,
      translateX: `-=${translateX}`,
      complete: () => {
        this.showArrowsIfNeeded()

        // "Convert" translateX into scrollLeft for touchscreens.
        // Alternatively, instead of animating "translateX", it also works to
        // animate "scrollLeft". This would make this converting obsolete.
        // However, there was a glitch that caused the scrollable element to
        // jump when hovering over the selected tab after scrolling.
        if (isTouchscreen()) {
          // Causes a scroll event
          scrollableEl.scrollTo(
            scrollableEl.scrollLeft + translateX,
            scrollableEl.scrollTop
          )

          anime.set(scrollableEl, { translateX: 0 })
        }
      },
    })

    return this.currentAnimation.finished
  }

  /**
   * Draws the layout. Useful to redraw the component when initially
   * rendered on a hidden parent (e.g. an sdx-header).
   */
  @Method()
  public async layout() {
    this.showArrowsIfNeeded()
  }

  public componentDidLoad() {
    this.showArrowsIfNeeded()

    this.componentDidLoadComplete = true
  }

  private getAnimationDuration(): number {
    if (!this.componentDidLoadComplete) {
      return 0
    }

    return this.animated ? 200 : 0
  }

  private getOverflow(to: "left" | "right"): number {
    const componentElRect = this.componentEl!.getBoundingClientRect()
    const slotElRect = this.slotEl!.getBoundingClientRect()
    const { scrollWidth } = this.scrollableEl!

    if (to === "left") {
      return componentElRect.left - slotElRect.left
    } else {
      // "right"
      return slotElRect.left + scrollWidth - componentElRect.right
    }
  }

  // getBoundingClientRect() does return floats, therefore it happens that
  // an element has an overflow of 0.666px, which is mathematically true, but
  // in reality does not count as a real overflow. Therefore check for 1
  // instead (which will be a "real" pixel, not a subpixel).
  private hasOverflow(to: "left" | "right"): boolean {
    if (to === "left") {
      return this.getOverflow("left") >= 1
    } else {
      // "right"
      return this.getOverflow("right") >= 1
    }
  }

  private showArrowsIfNeeded(): void {
    this.showLeftArrow = this.hasOverflow("left")
    this.showRightArrow = this.hasOverflow("right")
  }

  private onScroll(): void {
    if (this.scrollingTimer) {
      clearTimeout(this.scrollingTimer)
    }

    this.scrollingTimer = setTimeout(() => {
      this.onScrollDebounced()
    }, 30)
  }

  // Needed for touch initiated scrolling
  private onScrollDebounced(): void {
    this.showArrowsIfNeeded()
  }

  private onArrowClick(which: "left" | "right"): void {
    const componentElRect = this.componentEl!.getBoundingClientRect()

    // Scroll only by half of the available only so that end users don't lose
    // overview
    const translateX = componentElRect.width / 2

    this.translateX(which === "left" ? -translateX : translateX)
  }

  private getComponentClassNames() {
    return {
      component: true,
      "is-touchscreen": isTouchscreen(),
    }
  }

  public render() {
    return (
      <div
        class={this.getComponentClassNames()}
        ref={(el) => (this.componentEl = el)}
      >
        <div
          class="scrollable"
          onScroll={() => this.onScroll()}
          ref={(el) => (this.scrollableEl = el)}
        >
          <div class="slot" ref={(el) => (this.slotEl = el)}>
            <slot />
          </div>
        </div>

        <sdx-arrow
          class="left"
          direction="left"
          hidden={!this.showLeftArrow}
          onClick={() => this.onArrowClick("left")}
          animationDuration={this.getAnimationDuration()}
          arrowHidden={this.arrowsHidden}
          arrowPositionTop={this.arrowPositionTop}
        />

        <sdx-arrow
          class="right"
          direction="right"
          hidden={!this.showRightArrow}
          onClick={() => this.onArrowClick("right")}
          animationDuration={this.getAnimationDuration()}
          arrowHidden={this.arrowsHidden}
          arrowPositionTop={this.arrowPositionTop}
        />
      </div>
    )
  }
}
